// Visual timeline for Order detail V3 — lane-per-stage, one chip per session
// ("what was done · who did it"); click a chip for the full detail popup.
// Data comes from the same /api/orders/{id}/timeline feed as the V2 timeline.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    buildSessions, bundleChildSet, isBundleChildEvent, isCascadePressAutoPick,
    isSessionBoundary, consolidateAutoEvents, sessionDomain, parseGroupKey, eventLabel,
} from '../OrderTimeline';

export const PORTAL_VERB_LANE = { pick: 'Picked', press: 'Pressed', pack: 'Packed', print: 'Printed', other: 'Activity' };
const LANES = [
    { key: 'order', label: 'Order' },
    { key: 'print', label: 'Print' },
    { key: 'pick',  label: 'Pick' },
    { key: 'press', label: 'Press' },
    { key: 'pack',  label: 'Pack' },
    { key: 'ship',  label: 'Ship' },
    { key: 'other', label: 'Other' },
];
const LANE_TITLE = { order: 'Order placed', print: 'Printed', pick: 'Picking', press: 'Pressing', pack: 'Packing', ship: 'Shipment', other: 'Activity' };
const PORTAL_NAME = { picking: 'Picking Portal', pick: 'Picking Portal', pressing: 'Pressing Portal', press: 'Pressing Portal',
    packing: 'Packing Portal', pack: 'Packing Portal', print: 'Orders list', auto: 'Auto-complete' };
const DAY = 24 * 3600 * 1000;
const COLLAPSE = new Set(['pack_pass', 'pack_fail', 'bundle_finish', 'press_manual', 'pick', 'pick_scan', 'pick_manual']);

const fmtDay = (ms) => new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const fmtTime = (ms) => new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
const fmtFull = (ms) => `${new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · ${fmtTime(ms)}`;
const firstName = (n) => {
    const p = String(n || '').trim().split(/\s+/);
    return p.length > 1 ? `${p[0]} ${p[p.length - 1][0]}.` : (p[0] || '');
};

export function laneOf(session) {
    if (session.portal === 'print') return 'print';
    const dom = sessionDomain(session);
    const p = dom || session.portal;
    if (p === 'picking') return 'pick';
    if (p === 'pressing') return 'press';
    if (p === 'packing') return 'pack';
    return 'other';
}

function chipFor(ev) {
    const m = ev.payload?.method;
    switch (ev.event_type) {
        case 'pick_scan': return ['Scan', 'scan'];
        case 'pick': case 'pack_pass': case 'bundle_finish':
            if (m === 'scan') return ['Scan', 'scan'];
            return [ev.event_type === 'pack_pass' ? 'Pass' : ev.event_type === 'bundle_finish' ? 'Pressed' : 'Manual',
                    ev.event_type === 'pick' ? 'man' : ev.event_type === 'pack_pass' ? 'pass' : 'press'];
        case 'pick_manual': return ['Manual', 'man'];
        case 'press_manual': return ['Pressed', 'press'];
        case 'pack_fail': return ['Failed', 'bad'];
        case 'pick_unmark': return ['Unpicked', 'bad'];
        case 'press_unmark': return ['Unpressed', 'bad'];
        case 'pack_unmark': return ['Reset', 'bad'];
        case 'issue_reported': return ['Issue', 'bad'];
        default: return ['', ''];
    }
}

// Detail rows for one session (same grouping rules as the V2 session card).
export function sessionRows(session, itemsMap, allEvents) {
    const titleOf = (gk) => {
        const item = itemsMap[gk]; const parsed = parseGroupKey(gk);
        return { title: item?.title || parsed.base || '', sku: parsed.base || '', parsed, item };
    };
    if (session.portal === 'auto') {
        return consolidateAutoEvents(session.events, allEvents).map(entry => {
            const t = titleOf(entry.group_key);
            const chips = [entry.picked && 'Picked', entry.pressed && 'Pressed', entry.packed && 'Packed'].filter(Boolean).join(' + ');
            return { chip: chips, tone: 'scan', title: t.title, sku: t.sku, n: t.parsed.number ? `#${t.parsed.number}` : '' };
        });
    }
    const out = [];
    for (const ev of session.events.filter(e => !isCascadePressAutoPick(e) && !isSessionBoundary(e))) {
        const last = out[out.length - 1];
        if (last && last.ev.event_type === ev.event_type && last.ev.group_key === ev.group_key && COLLAPSE.has(ev.event_type)) {
            last.count += 1; continue;
        }
        out.push({ ev, count: 1 });
    }
    return out.map(({ ev, count }) => {
        const [chip, tone] = chipFor(ev);
        if (!ev.group_key || !/^(pick|press|bundle|pack)/.test(ev.event_type)) {
            return { chip, tone, title: eventLabel(ev), sku: '', n: '' };
        }
        const t = titleOf(ev.group_key);
        const qty = ev.event_type.startsWith('pack')
            ? Math.max(count, Number(t.item?.qty_total) || 1)
            : Math.max(count, Number(ev.qty_after) || 1);
        let title = t.title;
        if (ev.event_type === 'pack_fail') {
            const r = ev.payload?.reason ? ` — ${ev.payload.reason}` : '';
            title += r;
        }
        return {
            chip, tone, title, sku: t.sku,
            n: [t.parsed.player, t.parsed.number ? `#${t.parsed.number}` : '', qty > 1 ? `×${qty}` : ''].filter(Boolean).join(' '),
        };
    });
}

// One timeline/event-log entry for a work session; null when it has nothing to show.
// Store-pickup lifecycle events (transfer to the store, ready, collected) are all "Pickup" entries.
const PICKUP_TYPES = new Set(['store_transfer', 'store_scan_received', 'ready_for_pickup', 'picked_up']);

// Adds the fulfillment-order coverage ("10 of 10 items") and Shopify fulfillment number to a pickup mark.
export function withPickupInfo(m, pickupInfo) {
    if (!m || !m.isPickup || !pickupInfo) return m;
    const pk = pickupInfo(m.foIds);
    if (!pk.total && !pk.names.length) return m;
    return {
        ...m, pk,
        meta: [
            pk.total ? ['Items', `${pk.units} of ${pk.total}`] : null,
            pk.names.length ? ['Fulfillment', pk.names.join(', ')] : null,
            ...m.meta,
        ].filter(Boolean),
    };
}

export function sessionMark(s, i, { itemsMap, events, location }) {
    const lane = laneOf(s);
    const rows = sessionRows(s, itemsMap, events);
    if (!rows.length) return null;
    const keys = new Set(s.events.map(e => e.group_key).filter(Boolean));
    const who = s.portal === 'auto' ? 'Auto-completed on fulfillment' : (s.displayName || 'System');
    const items = keys.size || rows.length;
    const total = Object.values(itemsMap).filter(it => (it?.qty_total || 0) > 0).length;
    // Store pickups: who collected the order and what they signed (Team Portal pickup card, as in V1).
    const pickups = s.events.filter(e => e.event_type === 'picked_up' && (e.payload?.customer_name || e.payload?.signature_data_url))
        .map(e => ({ name: e.payload.customer_name || '', sig: e.payload.signature_data_url || '' }));
    const isPickup = s.events.length > 0 && s.events.every(e => PICKUP_TYPES.has(e.event_type));
    const foIds = [...new Set(s.events.flatMap(e => e.payload?.fulfillment_order_ids
        || (e.payload?.fulfillment_order_id ? [e.payload.fulfillment_order_id] : [])))];
    const countLbl = lane === 'press' ? 'Bundles pressed' : lane === 'pack' ? 'Items passed' : 'Items picked';
    const scans = s.events.filter(e => chipFor(e)[1] === 'scan').length;
    const manual = s.events.filter(e => chipFor(e)[1] === 'man').length;
    const label = lane === 'print'
        ? `Printed · ${firstName(who)}`
        : `${items} item${items === 1 ? '' : 's'} · ${firstName(who)}`;
    return {
        id: `s${i}`, lane, dot: lane === 'print', a: s.startTs, b: s.endTs > s.startTs ? s.endTs : null, label,
        title: isPickup ? 'Pickup' : LANE_TITLE[lane],
        when: s.endTs > s.startTs ? `${fmtFull(s.startTs)} – ${fmtTime(s.endTs)}` : fmtFull(s.startTs),
        who, items, rows, pickups, isPickup, foIds,
        portal: PORTAL_NAME[s.portal === 'admin' || s.portal === 'admin_console' ? lane : s.portal] || 'Admin',
        meta: [
            location ? ['Location', location] : null,
            (lane === 'print' || isPickup) ? null
                : [countLbl, (total && lane !== 'press' && total >= items) ? `${items} of ${total}` : String(items)],
            (scans || manual) ? ['Method', `${scans} scanned · ${manual} manual`] : null,
        ].filter(Boolean),
    };
}

// Meta grid + item rows, shared by the timeline popup and the event-log expansion.
export function EventDetailBody({ ev }) {
    return (
        <>
            {ev.meta.length > 0 && (
                <div className="odv3-ev-meta">
                    {ev.meta.map(([k, v]) => <React.Fragment key={k}><span>{k}</span><b>{v}</b></React.Fragment>)}
                </div>
            )}
            {(ev.pickups || []).map((p, i) => (
                <div key={i} className="odv3-pkcard">
                    {p.name && <div className="odv3-pkcard-n"><b>Customer Printed Name:</b> {p.name}</div>}
                    {p.sig && (
                        <div>
                            <div className="odv3-pkcard-l">Customer Signature</div>
                            <img src={p.sig} alt="Customer signature" className="odv3-pkcard-sig"/>
                        </div>
                    )}
                </div>
            ))}
            {ev.rows.length > 0 && (
                <div className="odv3-ev-rows">
                    {ev.rows.map((r, i) => (
                        <div key={i} className="odv3-ev-row">
                            {r.chip && <span className={`odv3-ev-chip odv3-ev-chip--${r.tone}`}>{r.chip}</span>}
                            <div>
                                <div className="odv3-ev-t">{r.title}</div>
                                {r.sku && <code className="odv3-sku">{r.sku}</code>}
                            </div>
                            <span className="odv3-ev-n">{r.n}</span>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
}

export default function VisualTimelineV3({ orderId, order, fulfillBy, fetchFn, pickupInfo }) {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(null);
    const [width, setWidth] = useState(0);
    const wrapRef = useRef(null);

    useEffect(() => {
        let alive = true;
        fetchFn(`/api/orders/${orderId}/timeline`)
            .then(r => r.ok ? r.json() : { events: [], items: {}, bundle_membership: {} })
            .then(d => { if (alive) setData(d); })
            .catch(() => { if (alive) setData({ events: [], items: {}, bundle_membership: {} }); });
        return () => { alive = false; };
    }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const el = wrapRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(() => setWidth(el.clientWidth));
        ro.observe(el); setWidth(el.clientWidth);
        return () => ro.disconnect();
    }, [data]);

    useEffect(() => {
        if (!open) return undefined;
        const k = (e) => { if (e.key === 'Escape') setOpen(null); };
        window.addEventListener('keydown', k);
        return () => window.removeEventListener('keydown', k);
    }, [open]);

    const model = useMemo(() => {
        if (!data) return null;
        const itemsMap = data.items || {};
        const bases = bundleChildSet(data.bundle_membership);
        const events = (data.events || []).filter(e => !isBundleChildEvent(e, bases, itemsMap));
        const placed = new Date(order?.processedAt || order?.createdAt).getTime();
        const locNames = [...new Set((order?.fulfillmentOrders?.edges || [])
            .map(e => e?.node?.assignedLocation?.name).filter(Boolean))];
        const location = locNames.length === 1 ? locNames[0] : '';
        const marks = []; // { id, lane, a, b, label, title, when, who, meta, rows }

        if (isFinite(placed)) {
            marks.push({
                id: 'order', lane: 'order', dot: true, a: placed, b: null, label: 'Order placed',
                title: 'Order placed', when: fmtFull(placed), who: order?.sourceName ? `Shopify · ${order.sourceName}` : 'Shopify', portal: '',
                meta: [['Order', order?.name || ''], ['Fulfill by', fulfillBy ? fmtFull(new Date(fulfillBy).getTime()) : '']].filter(m => m[1]),
                rows: [],
            });
        }
        buildSessions(events).forEach((s, i) => {
            const m = withPickupInfo(sessionMark(s, i, { itemsMap, events, location }), pickupInfo);
            if (m) marks.push(m);
        });
        (order?.fulfillments || []).forEach((f, i) => {
            const t = new Date(f.createdAt).getTime();
            if (!isFinite(t)) return;
            const tr = (f.trackingInfo || [])[0];
            marks.push({
                id: `f${i}`, lane: 'ship', dot: true, a: t, b: null, label: tr?.company ? `Fulfilled · ${tr.company}` : 'Fulfilled',
                title: 'Shipment fulfilled', when: fmtFull(t), who: 'Shopify', portal: '',
                meta: [tr?.company && ['Carrier', tr.company], tr?.number && ['Tracking', tr.number]].filter(Boolean),
                rows: [],
            });
        });

        const times = marks.flatMap(m => [m.a, m.b].filter(Boolean));
        const now = Date.now();
        const start = Math.min(...times, now);
        const lastEv = Math.max(...times, start);
        const startDay = new Date(start); startDay.setHours(0, 0, 0, 0);
        const end = Math.max(lastEv, now) + DAY * 0.5;
        const fb = fulfillBy ? new Date(fulfillBy).getTime() : null;
        const t0 = startDay.getTime();
        const span = Math.max(end - t0, DAY * 2);
        return { marks, t0, span, now, fb, hasLane: (k) => marks.some(m => m.lane === k) };
    }, [data, order, fulfillBy, pickupInfo]);

    if (!model) return <div className="odv3-stub">Loading timeline…</div>;
    const { marks, t0, span, now, fb } = model;
    const pct = (ms) => Math.min(100, Math.max(0, ((ms - t0) / span) * 100));

    // axis ticks: at most ~10, on day boundaries
    const days = Math.ceil(span / DAY);
    const step = Math.max(1, Math.ceil(days / 10));
    const ticks = [];
    for (let d = 0; d <= days; d += step) ticks.push(t0 + d * DAY);

    const laneKeys = LANES.filter(l => l.key === 'order' || model.hasLane(l.key));
    // label stacking so neighbouring chips don't overlap
    const trackPx = Math.max(300, width - 70 - 32);
    // Chip length reflects time spent: true scale where it is wide enough, otherwise a
    // compressed minimum (28px + √minutes, capped) so short sessions stay visible and
    // longer ones still read as longer. Dots (order / print / shipment) are fixed.
    const barPx = (m) => {
        if (m.dot) return 14;
        const mins = ((m.b || m.a) - m.a) / 60000;
        const trueW = ((pct(m.b || m.a) - pct(m.a)) / 100) * trackPx;
        return Math.max(trueW, Math.min(28 + 6 * Math.sqrt(mins), 170));
    };
    const placeLane = (list) => {
        const sorted = [...list].sort((x, y) => x.a - y.a);
        const rowsEnd = [];
        return sorted.map(m => {
            const left = (pct(m.a) / 100) * trackPx;
            const wpx = m.label.length * 6.4 + 18;
            const bw = barPx(m);
            let r = rowsEnd.findIndex(e => e + 6 <= left);
            if (r < 0) { r = rowsEnd.length; rowsEnd.push(0); }
            rowsEnd[r] = left + bw + 6 + wpx;
            return { m, r, bw };
        });
    };

    const ev = open ? marks.find(m => m.id === open) : null;

    return (
        <div className="odv3-vt-wrap" ref={wrapRef}>
            <div className="odv3-vt" style={{ minWidth: 760 }}>
                <div className="odv3-vt-row odv3-vt-axis">
                    <span/>
                    <div className="odv3-vt-track">
                        {ticks.map(t => <span key={t} style={{ left: `${pct(t)}%` }}>{fmtDay(t)}</span>)}
                    </div>
                </div>
                {laneKeys.map(l => {
                    const placed = placeLane(marks.filter(m => m.lane === l.key));
                    const rows = Math.max(1, ...placed.map(p => p.r + 1));
                    return (
                        <div key={l.key} className="odv3-vt-row" style={{ height: 40 * rows }}>
                            <span className="odv3-vt-lane">{l.label}</span>
                            <div className="odv3-vt-track">
                                {ticks.map(t => <i key={t} className="odv3-vt-grid" style={{ left: `${pct(t)}%` }}/>)}
                                <i className="odv3-vt-now" style={{ left: `${pct(now)}%` }}/>
                                {placed.map(({ m, r, bw }) => (
                                    <React.Fragment key={m.id}>
                                        {!m.dot
                                            ? <button type="button" className={`odv3-vt-bar odv3-c-${l.key}`}
                                                style={{ left: `${pct(m.a)}%`, width: bw, top: 13 + r * 40 }}
                                                aria-label={`${m.label} — details`} onClick={() => setOpen(m.id)}/>
                                            : <button type="button" className={`odv3-vt-pt odv3-c-${l.key}`}
                                                style={{ left: `${pct(m.a)}%`, top: 13 + r * 40 }}
                                                aria-label={`${m.label} — details`} onClick={() => setOpen(m.id)}/>}
                                        <button type="button" className="odv3-vt-lbl"
                                            style={{ left: `calc(${pct(m.a)}% + ${m.dot ? 12 : bw + 8}px)`, top: 12 + r * 40 }}
                                            onClick={() => setOpen(m.id)}>{m.label}</button>
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="odv3-vt-legend">
                {laneKeys.map(l => <span key={l.key}><i className={`odv3-c-${l.key}`}/>{l.label}</span>)}
                <span><i className="odv3-vt-nowkey"/>Now</span>
                {fb && (fb < t0 || fb > t0 + span) && <span className="odv3-vt-fbkey">Fulfill by {fmtDay(fb)} (off chart)</span>}
            </div>

            {ev && (
                <div className="odv3-ev-scrim" onClick={(e) => { if (e.target === e.currentTarget) setOpen(null); }}>
                    <div className="odv3-ev-dlg" role="dialog" aria-modal="true" aria-labelledby="odv3-ev-title">
                        <div className="odv3-ev-h">
                            <div>
                                <h2 id="odv3-ev-title"><span className={`odv3-ev-dot odv3-c-${ev.lane}`}/>{ev.title}</h2>
                                <div className="odv3-ev-sub">{[ev.when, ev.who, ev.portal].filter(Boolean).join(' · ')}</div>
                            </div>
                            <button type="button" className="odv3-ev-x" aria-label="Close" onClick={() => setOpen(null)}>×</button>
                        </div>
                        <div className="odv3-ev-b"><EventDetailBody ev={ev}/></div>
                    </div>
                </div>
            )}
        </div>
    );
}
